package info.rezident.app.ui.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowRight
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import info.rezident.app.data.quiz.PredefinedAnswer
import info.rezident.app.data.quiz.QuizQuestion
import info.rezident.app.data.quiz.UserAnswer
import info.rezident.app.ui.components.widgets.LargeTextArea
import info.rezident.app.ui.components.widgets.Spacer
import info.rezident.app.ui.components.widgets.placeholderColorStyle
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.builders.InputAttrsScope
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input

private const val OTHER_OPTION_KEY = "other"
private const val NEGATIVE_OPTION_TEXT = "Nu"
private const val CATEGORY_EXCLUSION_QUESTION_ID = 27
private const val CATEGORY_EXCLUSION_MAX_SELECTED_OPTIONS = 2

/**
 * StepScaffold is a composable that serves as a template for all quiz steps.
 *
 * @param title The title of the step
 * @param description The description of the step
 * @param questions The list of questions for this step
 * @param userAnswers List of existing answers to display
 * @param onNewAnswer Callback when answers are updated
 */
@Composable
fun StepScaffold(
    quizTitle: String? = null,
    stepTitle: String,
    description: String,
    questions: List<QuizQuestion>,
    userAnswers: List<UserAnswer>,
    onNewAnswer: (UserAnswer) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxSize(),
        horizontalAlignment = Alignment.Start
    ) {
        quizTitle?.let {
            SpanText(
                text = quizTitle,
                AppTheme.typography.h1.toModifier()
                    .margin(bottom = 16.px)
                    .color(AppTheme.colors.text)
                    .textDecorationLine(TextDecorationLine.Overline)
            )
        }

        // Step title
        SpanText(
            text = stepTitle,
            AppTheme.typography.h1.toModifier()
                .margin(bottom = 16.px)
                .color(AppTheme.colors.text)
        )

        // Step description
        SpanText(
            text = description,
            AppTheme.typography.body1.toModifier()
                .color(AppTheme.colors.textSecondary)
                .margin(bottom = 32.px)
        )

        // Render questions
        questions.forEachIndexed { index, question ->
            // Find the existing answer for this question if any
            val existingAnswer = userAnswers.find { it.questionId == question.id }

            Question(
                question = question,
                existingAnswer = existingAnswer,
                onAnswerCompleted = { quizAnswer ->
                    // Update the step answers map when a question is answered
                    onNewAnswer(quizAnswer)
                }
            )

            // Add spacing between questions
            if (index < questions.size - 1) {
                Spacer(modifier = Modifier.height(24.px))
            }
        }
    }
}

/**
 * Question is a generic composable for displaying questions with various answer types.
 *
 * @param question The question object
 * @param existingAnswer Previous answer for this question, if any
 * @param onAnswerCompleted Callback when the answer is updated
 */
@Composable
fun Question(
    question: QuizQuestion,
    existingAnswer: UserAnswer? = null,
    onAnswerCompleted: (UserAnswer) -> Unit = {}
) {
    Column(
        modifier = Modifier.fillMaxWidth()
            .margin(bottom = 28.px),
        horizontalAlignment = Alignment.Start
    ) {
        // Display factor text if present
        if (!question.factor.isNullOrBlank()) {
            SpanText(
                text = question.factor,
                AppTheme.typography.body2.toModifier()
                    .margin(bottom = 8.px)
                    .color(AppTheme.colors.textTertiary)
            )
        }

        SpanText(
            text = question.text,
            AppTheme.typography.h2.toModifier()
                .margin(bottom = 16.px)
                .color(AppTheme.colors.text)
        )

        // Render the appropriate UI based on question properties
        when {
            // Five-scale question
            question.fiveScale -> {
                // Initialize with existing five-scale answer if any
                var scaleValue by remember(question.id) { mutableStateOf(existingAnswer?.fiveScaleAnswer) }

                FiveScaleSlider(
                    value = scaleValue,
                    onValueChange = { newValue ->
                        scaleValue = newValue

                        // Create and emit the answer
                        val userAnswer = UserAnswer(
                            questionId = question.id,
                            answerIds = null,
                            openAnswer = null,
                            fiveScaleAnswer = newValue
                        )
                        onAnswerCompleted(userAnswer)
                    }
                )

                // Display scale explanation if present
                if (!question.scaleExplanation.isNullOrBlank()) {
                    SpanText(
                        text = question.scaleExplanation,
                        AppTheme.typography.body2.toModifier()
                            .color(AppTheme.colors.textTertiary)
                    )
                }
            }

            // Search-based question (like specialties selector)
            question.search -> {
                // Initialize with existing selected specialties if any
                val selectedSpecs = remember {
                    if (existingAnswer?.answerIds != null) {
                        question.predefinedPredefinedAnswers
                            .filter { predefinedAnswer ->
                                existingAnswer.answerIds.contains(predefinedAnswer.id)
                            }
                            .toMutableStateList()
                    } else {
                        mutableStateListOf<PredefinedAnswer>()
                    }
                }

                MedicalSpecialtiesSearchSelector(
                    predefinedAnswers = question.predefinedPredefinedAnswers,
                    selectedSpecialties = selectedSpecs,
                    onSelectionChange = { updatedSpecialties ->
                        selectedSpecs.clear()
                        selectedSpecs.addAll(updatedSpecialties)

                        // Create and emit the answer
                        val userAnswer = UserAnswer(
                            questionId = question.id,
                            answerIds = updatedSpecialties.map { it.id },
                        )
                        onAnswerCompleted(userAnswer)
                    }
                )
            }

            // Open text answer question
            question.openAnswerPlaceholder != null && question.predefinedPredefinedAnswers.isEmpty() -> {
                // Initialize with existing open text if any
                var openText by remember(question.id) { mutableStateOf(existingAnswer?.openAnswer ?: "") }

                OpenTextAnswer(
                    value = openText,
                    placeholder = question.openAnswerPlaceholder,
                    minLength = 5,
                    onValueChange = { newText ->
                        openText = newText

                        // Create and emit the answer
                        val userAnswer = UserAnswer(
                            questionId = question.id,
                            answerIds = null,
                            openAnswer = newText
                        )
                        onAnswerCompleted(userAnswer)
                    }
                )
            }

            else -> {
                val maxSelectedOptions = if (question.id == CATEGORY_EXCLUSION_QUESTION_ID) {
                    CATEGORY_EXCLUSION_MAX_SELECTED_OPTIONS
                } else {
                    null
                }
                val exclusiveOption = NEGATIVE_OPTION_TEXT.takeIf { !question.single }

                // For questions with predefined answers
                // Get the texts of selected answers
                val initialSelectedTexts = remember(question.id) {
                    if (existingAnswer?.answerIds != null) {
                        question.predefinedPredefinedAnswers
                            .filter { predefinedAnswer -> existingAnswer.answerIds.contains(predefinedAnswer.id) }
                            .map { it.text }
                            .let { answers ->
                                if (existingAnswer.openAnswer?.isNotBlank() == true) {
                                    (answers + OTHER_OPTION_KEY)
                                } else {
                                    answers
                                }
                            }
                            .enforceSelectionRules(maxSelectedOptions, exclusiveOption)
                            .toMutableStateList()
                    } else if (existingAnswer?.openAnswer?.isNotBlank() == true) {
                        mutableStateListOf(OTHER_OPTION_KEY)
                    } else {
                        mutableStateListOf()
                    }
                }

                var selectedAnswerTexts by remember(question.id) { mutableStateOf(initialSelectedTexts) }
                var openAnswerText by remember(question.id) { mutableStateOf(existingAnswer?.openAnswer ?: "") }

                Answers(
                    options = question.predefinedPredefinedAnswers.map { it.text },
                    selectedOptions = selectedAnswerTexts,
                    isSingleChoice = question.single,
                    maxSelectedOptions = maxSelectedOptions,
                    exclusiveOption = exclusiveOption,
                    hasOpenAnswer = question.openAnswerPlaceholder != null,
                    openAnswerText = openAnswerText,
                    openAnswerPlaceholder = question.openAnswerPlaceholder,
                    onSelectionChange = { newSelectedOptions ->
                        selectedAnswerTexts.clear()
                        selectedAnswerTexts.addAll(newSelectedOptions)

                        // Map selected text options back to answer IDs
                        val answerIds = question.predefinedPredefinedAnswers
                            .filter { selectedAnswerTexts.contains(it.text) }
                            .map { it.id }

                        // Create and emit the answer
                        val userAnswer = UserAnswer(
                            questionId = question.id,
                            answerIds = answerIds.takeIf { it.isNotEmpty() },
                            openAnswer = openAnswerText.takeIf { it.isNotBlank() }
                        )
                        onAnswerCompleted(userAnswer)
                    },
                    onSelectionWithCustomChange = { newSelectedOptions, newOpenText ->
                        selectedAnswerTexts.clear()
                        selectedAnswerTexts.addAll(newSelectedOptions)
                        openAnswerText = newOpenText

                        // Map selected text options back to answer IDs
                        val answerIds = question.predefinedPredefinedAnswers
                            .filter { selectedAnswerTexts.contains(it.text) }
                            .map { it.id }

                        // Create and emit the answer
                        val userAnswer = UserAnswer(
                            questionId = question.id,
                            answerIds = answerIds.takeIf { it.isNotEmpty() },
                            openAnswer = newOpenText.takeIf { it.isNotBlank() }
                        )
                        onAnswerCompleted(userAnswer)
                    }
                )

                // Display scale explanation if present
                if (!question.scaleExplanation.isNullOrBlank()) {
                    Spacer(Modifier.height(8.px))

                    SpanText(
                        text = question.scaleExplanation,
                        AppTheme.typography.body2.toModifier()
                            .color(AppTheme.colors.textTertiary)
                    )
                }
            }
        }
    }
}

/**
 * Unified Answers component that can handle both single-choice and multi-choice questions,
 * as well as questions with custom input options.
 *
 * @param options List of available options
 * @param selectedOptions List of currently selected options
 * @param isSingleChoice Whether the question allows only a single choice
 * @param hasOpenAnswer Whether to show a custom "Other" input field
 * @param openAnswerText Current text value for the custom input
 * @param openAnswerPlaceholder Placeholder text for the custom input
 * @param onSelectionChange Callback when selection changes (without custom option)
 * @param onSelectionWithCustomChange Callback when selection changes (with custom option)
 */
@Composable
fun Answers(
    options: List<String>,
    selectedOptions: List<String>,
    isSingleChoice: Boolean = true,
    maxSelectedOptions: Int? = null,
    exclusiveOption: String? = null,
    hasOpenAnswer: Boolean = false,
    openAnswerText: String = "",
    openAnswerPlaceholder: String? = "Specificați alte opțiuni (minim 5 caractere)",
    onSelectionChange: (List<String>) -> Unit = {},
    onSelectionWithCustomChange: (List<String>, String) -> Unit = { _, _ -> }
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.Start
    ) {
        // Regular options
        options.forEach { option ->
            val isSelected = selectedOptions.contains(option)

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(bottom = 8.px)
                    .padding(all = 12.px)
                    .backgroundColor(if (isSelected) AppTheme.colors.section else AppTheme.colors.surface)
                    .border(1.px, LineStyle.Solid, AppTheme.colors.text)
                    .borderRadius(8.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        val updatedSelection = if (isSingleChoice) {
                            // For single choice, either select the clicked option or deselect if already selected
                            if (isSelected) emptyList() else listOf(option)
                        } else {
                            updatedMultiSelection(
                                selectedOptions = selectedOptions,
                                option = option,
                                isSelected = isSelected,
                                maxSelectedOptions = maxSelectedOptions,
                                exclusiveOption = exclusiveOption
                            )
                        }

                        if (hasOpenAnswer) {
                            onSelectionWithCustomChange(updatedSelection, openAnswerText)
                        } else {
                            onSelectionChange(updatedSelection)
                        }
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    text = option,
                    AppTheme.typography.body1.toModifier()
                        .margin(left = 8.px)
                        .fontWeight(if (isSelected) FontWeight.Bold else FontWeight.Normal)
                        .color(AppTheme.colors.text)
                )
            }
        }

        // Custom "Other" option with input field
        if (hasOpenAnswer && openAnswerPlaceholder != null) {
            var otherTouched by remember(openAnswerText) { mutableStateOf(openAnswerText.isNotEmpty()) }
            var otherFocused by remember { mutableStateOf(false) }
            val showOtherError = otherTouched && !otherFocused && openAnswerText.length < 5
            val otherSelected = selectedOptions.contains(OTHER_OPTION_KEY) && !showOtherError

            CustomInputOption(
                inputValue = openAnswerText,
                isSelected = otherSelected,
                placeholder = openAnswerPlaceholder,
                onInputChange = { newValue ->
                    // Determine if the custom option should be selected based on text length
                    val shouldSelectCustom = openAnswerText.length >= 5 || newValue.length >= 5

                    // Update the selection based on the input length
                    val updatedSelection = if (isSingleChoice) {
                        // For single choice, either select custom option or keep current selection
                        if (shouldSelectCustom) {
                            listOf(OTHER_OPTION_KEY)
                        } else if (otherSelected) {
                            emptyList() // Deselect custom if text is too short
                        } else {
                            selectedOptions // Keep current selection
                        }
                    } else {
                        // For multi choice, toggle the custom option based on text length
                        val updated = selectedOptions.toMutableList()
                        if (shouldSelectCustom && !otherSelected) {
                            exclusiveOption?.let { updated.remove(it) }
                            if (maxSelectedOptions != null && updated.size >= maxSelectedOptions) {
                                updated.removeAt(0)
                            }
                            updated.add(OTHER_OPTION_KEY)
                        } else if (!shouldSelectCustom && otherSelected) {
                            updated.remove(OTHER_OPTION_KEY)
                        }
                        updated
                    }

                    onSelectionWithCustomChange(updatedSelection, newValue)
                },
                onFocus = {
                    otherFocused = true
                    otherTouched = true
                },
                onBlur = {
                    otherFocused = false
                },
                showError = showOtherError
            )

            if (showOtherError) {
                ErrorMessage("Introduceți minim 5 caractere")
            }
        }
    }
}

private fun updatedMultiSelection(
    selectedOptions: List<String>,
    option: String,
    isSelected: Boolean,
    maxSelectedOptions: Int?,
    exclusiveOption: String?
): List<String> {
    val updated = selectedOptions.toMutableList()

    if (isSelected) {
        updated.remove(option)
        return updated
    }

    if (option == exclusiveOption) {
        return listOf(option)
    }

    exclusiveOption?.let { updated.remove(it) }
    if (maxSelectedOptions != null && updated.size >= maxSelectedOptions) {
        updated.removeAt(0)
    }
    updated.add(option)

    return updated
}

private fun List<String>.enforceSelectionRules(
    maxSelectedOptions: Int?,
    exclusiveOption: String?
): List<String> {
    if (exclusiveOption != null && exclusiveOption in this) {
        return listOf(exclusiveOption)
    }

    return if (maxSelectedOptions != null) {
        take(maxSelectedOptions)
    } else {
        this
    }
}

/**
 * OpenTextAnswer displays a text area for open-ended responses.
 *
 * @param value Current text value
 * @param placeholder Placeholder text
 * @param minLength The minimum required length
 * @param onValueChange Callback when text changes
 */
@Composable
fun OpenTextAnswer(
    value: String,
    placeholder: String,
    minLength: Int,
    onValueChange: (String) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 16.px)
            .backgroundColor(AppTheme.colors.surface)
    ) {
        LargeTextArea(
            value = value,
            placeholder = placeholder,
            unfocusedBorderColor = AppTheme.colors.text,
            paddingLeftRight = 20.px,
            paddingTopBottom = 12.px,
            onValueChange = onValueChange
        )
    }
}

/**
 * CustomInputOption displays an option with a custom text input field.
 *
 * @param inputValue Current input text
 * @param isSelected Whether this option is selected
 * @param placeholder Placeholder text
 * @param onInputChange Callback when input changes
 * @param onFocus Callback when input receives focus
 * @param onBlur Callback when input loses focus
 * @param showError Whether to show error styling
 */
@Composable
fun CustomInputOption(
    inputValue: String,
    isSelected: Boolean,
    placeholder: String,
    onInputChange: (String) -> Unit,
    onFocus: () -> Unit,
    onBlur: () -> Unit,
    showError: Boolean = false
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = if (showError) 0.px else 8.px)
            .borderRadius(8.px)
            .backgroundColor(if (isSelected) AppTheme.colors.section else AppTheme.colors.surface),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val placeholderAttrs = placeholderColorStyle.toAttrs<InputAttrsScope<String>>()
        val textStyle = AppTheme.typography.body1.toModifier().then(Modifier.color(AppTheme.colors.text))
            .toAttrs<InputAttrsScope<String>>()
        val borderColor = AppTheme.colors.text
        val errorColor = AppTheme.colors.error

        Input(
            type = InputType.Text,
            attrs = {
                style {
                    width(100.percent)
                    padding(12.px, 12.px, 12.px, 20.px)
                    backgroundColor(Color.transparent)
                    border(1.px, LineStyle.Solid, if (showError) errorColor else borderColor)
                    borderRadius(8.px)
                    boxSizing("border-box")
                    fontWeight(if (isSelected) FontWeight.Bold else FontWeight.Normal)
                }
                textStyle(this)
                value(inputValue)
                onInput { event ->
                    onInputChange(event.value)
                }
                onFocus {
                    onFocus()
                }
                onBlur {
                    onBlur()
                }
                placeholder(placeholder)
                placeholderAttrs(this)
            }
        )
    }
}

@Composable
fun ErrorMessage(text: String) {
    SpanText(
        text = text,
        AppTheme.typography.body1.toModifier()
            .margin(top = 4.px, left = 8.px)
            .fontSize(14.px)
            .color(AppTheme.colors.error)
    )
}

/**
 * FiveScaleSlider displays a slider with 5 steps for five-scale questions.
 *
 * @param value Current selected value (1-5, or null if not selected)
 * @param onValueChange Callback when value changes
 */
@Composable
fun FiveScaleSlider(
    value: Int?,
    onValueChange: (Int) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(top = 4.px, bottom = 20.px),
        horizontalAlignment = Alignment.Start
    ) {
        // Slider track with steps
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .minHeight(56.px)
                .padding(leftRight = if (breakpoint < Breakpoint.MD) 8.px else 16.px)
                .position(Position.Relative),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Background track
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(5.px)
                    .backgroundColor(AppTheme.colors.textTertiary)
                    .borderRadius(2.px)
                    .position(Position.Absolute)
                    .top(50.percent)
                    .left(0.px)
                    .styleModifier {
                        property("transform", "translateY(-50%)")
                    }
            )

            // Active track (from start to selected value)
            if (value != null) {
                Box(
                    modifier = Modifier
                        .width(((value - 1) * 25).percent)
                        .height(5.px)
                        .backgroundColor(AppTheme.colors.accent)
                        .borderRadius(2.px)
                        .position(Position.Absolute)
                        .top(50.percent)
                        .left(0.px)
                        .styleModifier {
                            property("transform", "translateY(-50%)")
                            property("transition", "width 0.2s ease-in-out")
                        }
                )
            }

            // Steps
            (1..5).forEach { step ->
                val isSelected = value == step
                val isPassed = value != null && step <= value

                Box(
                    modifier = Modifier
                        .size(if (isSelected) 56.px else 48.px)
                        .backgroundColor(
                            when {
                                isSelected -> AppTheme.colors.accent
                                isPassed -> AppTheme.colors.accent
                                else -> AppTheme.colors.surface
                            }
                        )
                        .border(
                            width = 3.px,
                            style = LineStyle.Solid,
                            color = if (isPassed || isSelected) AppTheme.colors.accent else AppTheme.colors.textTertiary
                        )
                        .borderRadius(50.percent)
                        .cursor(Cursor.Pointer)
                        .onClick { onValueChange(step) }
                        .styleModifier {
                            property("transition", "all 0.2s ease-in-out")
                        }
                        .zIndex(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = step.toString(),
                        modifier = if (isSelected) {
                            AppTheme.typography.body1Medium.toModifier()
                        } else {
                            AppTheme.typography.body1.toModifier()
                        }
                            .color(
                                if (isPassed || isSelected) AppTheme.colors.onAccent else AppTheme.colors.textSecondary
                            )
                    )
                }
            }
        }
    }
}

/**
 * Navigation bar for the quiz
 * @param currentStep Current step index
 * @param canGoBack Whether the back button should be enabled
 * @param isLastStep Whether this is the last step
 * @param isTransitioning Whether a transition is in progress
 * @param canGoForward Whether all mandatory questions have been answered to proceed
 * @param nextLabel Custom label for the next button (defaults to "Continuă" or "Finalizează")
 * @param isLoading Whether the next button should show loading state
 * @param onBackClick Callback for previous button
 * @param onContinueClick Callback for next button
 */
@Composable
fun NavigationBar(
    currentStep: Int,
    canGoBack: Boolean,
    canGoForward: Boolean = true,
    isLastStep: Boolean,
    isTransitioning: Boolean,
    nextLabel: String? = null,
    isLoading: Boolean = false,
    onBackClick: () -> Unit,
    onContinueClick: () -> Unit
) {
    val breakpoint = rememberBreakpoint()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Fixed)
            .bottom(0.px)
            .backgroundColor(AppTheme.colors.surface)
            .padding(topBottom = 12.px, leftRight = 24.px)
            .boxShadow(0.px, (-2).px, 4.px, 0.px, rgba(0, 0, 0, 0.1f))
            .zIndex(100),
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().thenIf(
                breakpoint >= Breakpoint.MD,
                Modifier.maxWidth(1200.px)
            ),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (canGoBack) {
                Button(
                    onClick = {
                        if (!isTransitioning) {
                            onBackClick()
                        }
                    },
                    modifier = Modifier
                        .padding(all = 8.px)
                        .backgroundColor(Color.transparent)
                ) {
                    FaArrowLeft(
                        modifier = Modifier.color(AppTheme.colors.text)
                    )
                    SpanText(
                        text = "Înapoi",
                        modifier = AppTheme.typography.body1Medium.toModifier()
                            .margin(left = 8.px)
                            .color(AppTheme.colors.text)
                    )
                }
            } else {
                Box {}
            }

            if (!isLastStep) {
                Button(
                    onClick = {
                        if (!isTransitioning && canGoForward && !isLoading) {
                            onContinueClick()
                        }
                    },
                    modifier = Modifier
                        .backgroundColor(if (canGoForward && !isLoading) AppTheme.colors.accent else AppTheme.colors.textTertiary)
                        .borderRadius(AppTheme.shapes.mediumRound)
                        .padding(leftRight = 16.px, topBottom = 8.px)
                        .minWidth(140.px)
                        .height(48.px)
                        .fontSize(16.px)
                        .opacity(if (canGoForward && !isLoading) 1f else 0.9f)
                        .cursor(if (canGoForward && !isLoading) Cursor.Pointer else Cursor.Default),
                    enabled = canGoForward && !isLoading
                ) {
                    if (isLoading) {
                        // Show loading spinner
                        Box(
                            modifier = Modifier
                                .size(16.px)
                                .backgroundColor(Color.transparent)
                                .borderRadius(50.percent)
                                .border(2.px, LineStyle.Solid, AppTheme.colors.onAccent)
                                .borderTop(2.px, LineStyle.Solid, Color.transparent)
                                .styleModifier {
                                    property("animation", "spin 1s linear infinite")
                                },
                        )
                        SpanText(
                            text = nextLabel ?: "Se salvează...",
                            modifier = AppTheme.typography.body1Medium.toModifier()
                                .margin(left = 8.px)
                                .color(AppTheme.colors.onAccent)
                        )
                    } else {
                        SpanText(
                            text = nextLabel ?: if (!isLastStep) "Continuă" else "Finalizează",
                            modifier = AppTheme.typography.body1Medium.toModifier()
                                .margin(right = 8.px)
                                .color(if (canGoForward) AppTheme.colors.onAccent else AppTheme.colors.textSecondary)
                        )
                        FaArrowRight(
                            Modifier.color(
                                if (canGoForward)
                                    AppTheme.colors.onAccent
                                else
                                    AppTheme.colors.textSecondary
                            )
                        )
                    }
                }
            }
        }
    }
}

/**
 * SearchableSelector displays a search input with selectable items.
 * Specifically used for medical specialties selection.
 */
@Composable
fun MedicalSpecialtiesSearchSelector(
    predefinedAnswers: List<PredefinedAnswer>,
    selectedSpecialties: List<PredefinedAnswer>,
    onSelectionChange: (List<PredefinedAnswer>) -> Unit
) {
    var searchQuery by remember { mutableStateOf("") }
    val selectedItems = remember { mutableStateListOf(*selectedSpecialties.toTypedArray()) }

    Column(
        modifier = Modifier.fillMaxSize(),
        horizontalAlignment = Alignment.Start
    ) {
        // Search input
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .margin(bottom = 16.px)
        ) {
            val placeholderAttrs = placeholderColorStyle.toAttrs<InputAttrsScope<String>>()
            val textTertiary = AppTheme.colors.textTertiary
            Input(
                type = InputType.Text,
                attrs = {
                    style {
                        width(100.percent)
                        padding(12.px)
                        backgroundColor(Color.transparent)
                        border(1.px, LineStyle.Solid, textTertiary)
                        borderRadius(8.px)
                        boxSizing("border-box")
                        fontSize(16.px)
                    }
                    value(searchQuery)
                    onInput { event ->
                        searchQuery = event.value
                    }
                    placeholder("Caută o specialitate...")
                    placeholderAttrs(this)
                }
            )
        }

        // Selected specialties (chips)
        if (selectedItems.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(bottom = 16.px)
                    .padding(8.px)
                    .backgroundColor(AppTheme.colors.surface)
                    .border(1.px, LineStyle.Solid, AppTheme.colors.text)
                    .borderRadius(8.px)
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth()
                ) {
                    SpanText(
                        text = "Specialități selectate",
                        AppTheme.typography.body1Medium.toModifier()
                            .margin(bottom = 8.px)
                            .color(AppTheme.colors.text)
                    )

                    // Use Row with wrapping and styling
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                flexWrap(FlexWrap.Wrap)
                                gap(8.px)
                                width(100.percent)
                            }
                        }
                    ) {
                        selectedItems.forEach { specialty ->
                            Box(
                                modifier = Modifier
                                    .margin(4.px)
                                    .padding(leftRight = 12.px, topBottom = 6.px)
                                    .backgroundColor(AppTheme.colors.section)
                                    .borderRadius(50.px)
                                    .display(DisplayStyle.InlineBlock)
                            ) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    SpanText(
                                        text = specialty.text,
                                        AppTheme.typography.body1.toModifier()
                                            .margin(right = 8.px)
                                            .fontWeight(FontWeight.SemiBold)
                                            .fontSize(14.px)
                                            .color(AppTheme.colors.text)
                                    )

                                    Box(
                                        modifier = Modifier
                                            .size(18.px)
                                            .backgroundColor(AppTheme.colors.section)
                                            .borderRadius(50.percent)
                                            .cursor(Cursor.Pointer)
                                            .onClick {
                                                selectedItems.remove(specialty)
                                                onSelectionChange(selectedItems.toList())
                                            },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        SpanText(
                                            text = "×",
                                            AppTheme.typography.body1.toModifier()
                                                .color(AppTheme.colors.text)
                                                .fontSize(14.px)
                                                .fontWeight(FontWeight.Bold)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // List of specialties based on search
        val filteredSpecialties = if (searchQuery.isEmpty()) {
            predefinedAnswers.toList()
        } else {
            val normalizedQuery = searchQuery.trim().lowercase()
            val (startsWith, others) = predefinedAnswers.partition {
                it.text.lowercase().startsWith(normalizedQuery)
            }
            startsWith + others.filter {
                it.text.lowercase().contains(normalizedQuery) && !it.text.lowercase().startsWith(normalizedQuery)
            }
        }

        val textTertiary = AppTheme.colors.textTertiary
        Div(
            attrs = {
                style {
                    width(100.percent)
                    maxHeight(300.px)
                    property("overflow-y", "auto")
                    borderRadius(8.px)
                    border(1.px, LineStyle.Solid, textTertiary)
                }
            }
        ) {
            Column(
                modifier = Modifier.fillMaxWidth()
            ) {
                if (filteredSpecialties.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.px),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            text = "Nu am găsit nicio specialitate care să corespundă căutării tale",
                            AppTheme.typography.body1.toModifier()
                                .color(AppTheme.colors.textSecondary)
                        )
                    }
                } else {
                    filteredSpecialties.forEach { specialty ->
                        val isSelected = selectedItems.contains(specialty)
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(12.px)
                                .backgroundColor(if (isSelected) AppTheme.colors.section else AppTheme.colors.surface)
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    if (isSelected) {
                                        selectedItems.remove(specialty)
                                    } else {
                                        selectedItems.add(specialty)
                                    }
                                    onSelectionChange(selectedItems.toList())
                                },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            SpanText(
                                text = specialty.text,
                                AppTheme.typography.body1.toModifier()
                                    .fontWeight(if (isSelected) FontWeight.Bold else FontWeight.Normal)
                                    .color(AppTheme.colors.text)
                                    .textAlign(TextAlign.Start)
                            )

                            if (isSelected) {
                                Box(
                                    modifier = Modifier
                                        .size(24.px)
                                        .backgroundColor(AppTheme.colors.section)
                                        .borderRadius(50.percent),
                                    contentAlignment = Alignment.Center
                                ) {
                                    SpanText(
                                        text = "✓",
                                        AppTheme.typography.body1.toModifier()
                                            .color(AppTheme.colors.text)
                                            .fontSize(14.px)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
} 
